// ---------------------------------------------------------------------------
// decline.ts -- the structured "declined write" surface.
//
// When a data-plane write violates a proven policy rule or invariant, the
// realtime backend rejects it: HTTP 403 { verdict:'declined', reason:'rule' }
// or 409 { verdict:'declined', reason:'invariant', invariant } on the request
// path, or a coded error frame over the room socket. This module turns those
// rejections into a first-class, share-worthy decline:
//
//   - `BoundedDeclineError` -- the thrown error. Keeps every legacy field apps
//     already catch (code/status/statusCode/requestId/...), and adds `.decline`
//     (the structured object below) plus a stable `.isBoundedDecline` flag.
//   - `deriveDecline(body, status)` -- normalizes a server rejection body into a
//     `BoundedDecline`, trusting a backend-sent `decline` object when present
//     and synthesizing a minimal one from legacy fields otherwise. Returns
//     `null` when the rejection is not a policy/invariant decline.
//   - `dispatchDeclineEvent(decline, appId?)` -- in a browser, fires the
//     `bounded:decline` CustomEvent the hosted widget listens to. Fire-and-
//     forget: guarded for non-browser/SSR, never throws, never delays the throw.
// ---------------------------------------------------------------------------
const RULE_DEFAULT_MESSAGE = 'This write is not allowed by policy.';
const INVARIANT_DEFAULT_MESSAGE = 'This write would violate a proven invariant.';
/** The window event the hosted widget listens to for rendering a decline card. */
export const DECLINE_EVENT_NAME = 'bounded:decline';
/**
 * Normalize a server rejection body into a structured `BoundedDecline`, or
 * `null` when the rejection is not a policy/invariant decline.
 *
 * Prefers a backend-sent `decline` object verbatim; otherwise synthesizes a
 * minimal `{ verdict, reason, invariant?, message }` from the legacy envelope
 * (`verdict` / `reason` / `code` / `invariant`). Only strong signals count —
 * a bare 403/409 with no policy marker is left alone (returns `null`).
 */
export function deriveDecline(body, status) {
    if (!body || typeof body !== 'object')
        return null;
    // Trust a backend-sent structured decline verbatim.
    const provided = body.decline;
    if (provided && typeof provided === 'object' && provided.verdict === 'declined') {
        return provided;
    }
    const rawReason = body.reason;
    const code = body.code;
    const hasInvariant = body.invariant != null;
    let reason = rawReason === 'rule' || rawReason === 'invariant' ? rawReason : undefined;
    if (!reason) {
        if (code === 'invariant_violation' || hasInvariant)
            reason = 'invariant';
        else if (code === 'policy_denied')
            reason = 'rule';
    }
    const isDecline = body.verdict === 'declined' || reason === 'rule' || reason === 'invariant';
    if (!isDecline)
        return null;
    const resolvedReason = reason !== null && reason !== void 0 ? reason : (status === 409 ? 'invariant' : 'rule');
    const rawMessage = body.message;
    const message = typeof rawMessage === 'string' && rawMessage.length > 0
        ? rawMessage
        : resolvedReason === 'invariant'
            ? INVARIANT_DEFAULT_MESSAGE
            : RULE_DEFAULT_MESSAGE;
    const decline = { verdict: 'declined', reason: resolvedReason, message };
    if (hasInvariant)
        decline.invariant = body.invariant;
    if (typeof body.collection === 'string')
        decline.collection = body.collection;
    if (typeof body.op === 'string')
        decline.op = body.op;
    if (body.boundary && typeof body.boundary === 'object')
        decline.boundary = body.boundary;
    if (typeof body.provenAtDeploy === 'boolean')
        decline.provenAtDeploy = body.provenAtDeploy;
    return decline;
}
/**
 * In a browser, dispatch a `bounded:decline` CustomEvent for the surfaced
 * decline. Fire-and-forget: guarded for non-browser/SSR (no `window`/
 * `CustomEvent`), never throws, and never delays the rejection it accompanies.
 */
export function dispatchDeclineEvent(decline, appId) {
    try {
        if (typeof window === 'undefined')
            return;
        const Ctor = globalThis.CustomEvent;
        if (typeof Ctor !== 'function')
            return;
        const detail = Object.assign({}, decline);
        if (appId)
            detail.appId = appId;
        detail.at = Date.now();
        window.dispatchEvent(new Ctor(DECLINE_EVENT_NAME, { detail }));
    }
    catch (_a) {
        // Fire-and-forget: a widget/telemetry hiccup must never mask the decline.
    }
}
/**
 * A rejected write that violated a proven policy rule or invariant. Thrown by
 * the data-plane write paths (set/setMany/update/delete) in place of a bare
 * Error, and attached (as `.decline`) to live-intent rejections.
 *
 * Back-compatible: the full server envelope (code/status/statusCode/requestId/
 * ...) is copied onto the instance, so existing `catch` handlers keep working;
 * new code reads `.decline`. Detect it with `err.name === 'BoundedDeclineError'`
 * or the bundle-copy-safe `err.isBoundedDecline === true`.
 */
export class BoundedDeclineError extends Error {
    constructor(decline, envelope) {
        super(`Declined: ${decline.message}`);
        // Copy the legacy envelope FIRST, then re-assert our own fields so a
        // stray envelope key can never clobber name/message/decline/flag.
        if (envelope && typeof envelope === 'object')
            Object.assign(this, envelope);
        this.name = 'BoundedDeclineError';
        this.message = `Declined: ${decline.message}`;
        this.decline = decline;
        this.isBoundedDecline = true;
    }
}
